import { useRoute } from 'vue-router'
import useGet from '../../../core/composables/api/useGet'
import { computed, watch, toValue, type MaybeRef } from 'vue'
import type { FeedbackTopic } from '../../../core/models/feedback-topic'
import type { PulseQuestionAnalysis } from '@/core/models/surveys-analytics'
import { DialogPhase } from '@/core/models/dialog/dialog'
import { dialogKey, dialogRoleKey } from './use-dialog-navigation'
import { injectStrict } from '@/core/utils/inject-strict'

export const useFinalPulseSlide = (feedbackTopicId: MaybeRef<string>) => {
  const route = useRoute()

  const url = computed(() => {
    return `dialog/${route.params.id}/pulse-slide/${toValue(feedbackTopicId)}`
  })

  const dialog = injectStrict(dialogKey)
  const dialogRole = injectStrict(dialogRoleKey)

  const { data, load, status } = useGet<FinalDialogPulseSlideData>(url)

  const canSeeData = computed(() => {
    return (
      (dialogRole === 'coach' && dialog.phase === DialogPhase.CoachPreparation) ||
      (dialogRole === 'coach' && dialog.phase === DialogPhase.Conversation) ||
      (dialogRole === 'coach' && dialog.phase === DialogPhase.Conducted) ||
      dialogRole === 'coachee'
    )
  })

  const loadData = () => {
    if (canSeeData.value) {
      load()
    }
  }

  watch(
    url,
    () => {
      loadData()
    },
    { immediate: true },
  )

  return { data, status, canSeeData }
}

/** Released pulse slide: no texts, no feedbackSenders (never leave the coachee). */
export type FinalDialogPulseSlideData = {
  feedbackTopic: FeedbackTopic
  questions: ReadonlyArray<PulseQuestionAnalysis>
  numberOfSelfFeedbacks: number
  numberOfPeerFeedbacks: number
  numberOfExternalFeedbacks: number
  numberOfExternalSenders: number
}
